import React from 'react';
import {Button, Empty, Popover, Table} from "antd";
import {gray, orange, red} from "@ant-design/colors";
import {CloseOutlined, LoadingOutlined, QuestionCircleFilled, UploadOutlined, WarningOutlined} from "@ant-design/icons";
import {useUploadAnnotator} from "../../../../providers/mortgage/uploads/UploadAnnotatorContext";
import dayjs from "dayjs";

export default TableOfUploads;

export function TableOfUploads({section}) {


    const inActive = (section.uploads || []).filter(it => !it.isActive)
    const [viewInactive, setViewInactive] = React.useState(false);

    const onHeaderCell = () => ({
        style: {paddingTop: 6, paddingBottom: 6, fontWeight: 'normal'}
    })
    const columns = [
        {
            key: 'createdAt',
            title: <div style={{whiteSpace: 'nowrap'}}>
                {inActive.length > 0 && <>
                    <span className="me-15"> Hidden Uploads: {inActive.length} </span>
                    <Button type="link" onClick={() => setViewInactive(!viewInactive)}>
                        {viewInactive ? 'Hide' : 'Show'}
                    </Button>
                </>}
            </div>,
            dataIndex: 'createdAt',
            onHeaderCell,
            render: ((text, record) => {
                return <CreatedAtLabel record={record}/>
            })
        },
        {
            key: 'size',
            title: 'Size',
            dataIndex: 'size',
        },
        {
            key: 'queries',
            title: 'Queries',
            render: (text, record) => {
               return  record?.queries?.length || 0 > 0 ? <QuestionCircleFilled style={{color:orange.primary}}/> : null;
            }
        },
        {
            key: 'status',
            title: '',
            onHeaderCell,
            dataIndex: 'adminStatus',
            render: (text, record) => {
                const colorStyle = !record.isActive ? {color: gray.primary} : {}
                return <div style={{whiteSpace: 'nowrap', ...colorStyle}}>{record.adminStatus}</div>
            }
        },
        {
            key: 'queriesOS',
            title: '',
            onHeaderCell,
            dataIndex: 'adminStatus',
            render: (text, record) => {

                const q = record.missingUserAnswers ? <QuestionCircleFilled style={{color:orange.primary}}/> : null;
                const colorStyle = !record.isActive ? {color: gray.primary} : {}
                return <div style={{whiteSpace: 'nowrap', ...colorStyle}}> {q} </div>
            }
        },
        {
            key: 'data',
            title: 'Dated',
            onHeaderCell,
            render: (record) => {

                const colorStyle = !record.isActive ? {color: gray.primary} : {}
                if (!record.isVerifiable){
                    return {
                        children: 'Supplementary Document',
                        props: {
                            colSpan: 2
                        }
                    }
                }


                if (!record?.dateData?.dated){
                    return <div style={{whiteSpace: 'nowrap', color: gray.primary}}>No Date</div>
                }
                return <div style={{whiteSpace: 'nowrap', ...colorStyle}} className="d-row gap-6">
                    {record.dateData.dated.format('D MMM YY')}
                </div>
            }
        },
        {
            key: 'data',
            title: 'Covers',
            dataIndex: 'dateData',
            onHeaderCell,
            render: (text, record) => {
                if (!record.isVerifiable){
                    return {
                        children:null,
                        props: {
                            colSpan: 0
                        }
                    }
                }



                const {from, to} = text?.range || {};
                const colorStyle = !record.isActive ? {color: gray.primary} : {}
                let string = text ? 'Up to ' + text?.expires?.format('D MMM YY') || 'No Expiry': 'No Expiry';
                if (text?.range) {
                    let start = from?.date ? from.date.format('D MMM YY') : 'No Start';
                    let end = to?.date ? to.date.format('D MMM YY') : 'No End';
                    string = `${start} - ${end}`;
                }
                return <div style={{whiteSpace: 'nowrap', ...colorStyle}} className="d-row gap-6">
                    {string}
                    {record?.problems?.length > 0 && (
                        <Popover title="Errors" content={<div>{record.problems.map(e => {
                            return <div key={e}><CloseOutlined style={{color: red.primary}}/> {e}</div>
                        })}</div>}>
                            <WarningOutlined style={{marginLeft: 'auto', color: red.primary}}/>
                        </Popover>
                    )}
                </div>
            }
        },
    ]
    const {openAnnotator} = useUploadAnnotator()
    const sourceUploads = viewInactive ? (section.uploads || []) : (section.uploads || []).filter(it => it.isActive);
    console.log({sourceUploads})
    return <Table
        key={section.name + '+' + section.index}
        style={{borderRight: '1px solid #f0f0f0', width: '100%'}}
        rowHoverable
        rowKey="id"
        columns={columns}
        dataSource={sourceUploads.map(upload => ({...upload,size: (upload.props.upload.fileSize / 1048576).toFixed(2)}))}
        pagination={false}
        locale={{
            emptyText: (
                <Empty
                    description={null}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{padding: 0, margin: 0}}
                />
            ),
        }}
        onRow={(record) => {
            return {
                onClick: () => {
                    openAnnotator(record.id);
                },
                style: {
                    cursor: 'pointer',
                }
            }
        }}/>
}

function CreatedAtLabel({record}) {
    /**
     * Estimate upload time
     * @param {number} fileSizeMB - File size in megabytes
     * @returns {number} - Estimated upload time in seconds
     */
    function estimateUploadTime(fileSizeB) {
        const fileSizeMB = (fileSizeB || 0) / (1024 * 1024);
        const factor = 13 / 60; // ≈ 0.2167 sec per MB
        const time = fileSizeMB * factor;
        return Math.max(6, Math.round(time)) * 1000
    }

    const colorStyle = !record.isActive ? {color: gray.primary} : {}
    const d = dayjs(record.props.upload.createdAt);

    const now = dayjs();
    const [busy, setBusy] = React.useState(now.diff(d, 'second') < 6);
    const timerRef = React.useRef(null);
    React.useEffect(() => {
        timerRef.current = setTimeout(() => {
            if (busy){
                setBusy(false);
            }
        }, estimateUploadTime(record.props.upload.fileSize)); // 1 seconds
        return () => clearTimeout(timerRef.current);
    }, []);
    return <div style={{whiteSpace: 'nowrap', ...colorStyle}} className="d-row gap-6">
        {busy ? <LoadingOutlined/> : <UploadOutlined/>}
        <span>{d.format('D MMM YY')}</span>
    </div>
}
